import * as z from 'zod';

/**
 * One integer for the whole catalog. Adding an event or an optional
 * parameter does not bump it; renaming, removing, or changing a meaning or
 * a threshold does (`improvements/23-appendix-event-catalog.md`).
 */
// 2 (2026-10-19): `article_open`/`article_read`/`article_complete` no
// longer always carry `page_number` -- absent for an article read on its
// own page, outside its issue.
export const SCHEMA_VERSION = 2;

/** The longest string a destination is guaranteed to keep (GA4's limit). */
export const MAX_STRING_LENGTH = 100;

/** Envelope + content context + an event's own parameters (GA4's limit). */
export const MAX_PARAMETERS_PER_EVENT = 25;

/** A string that is cut to fit, never rejected for being long. */
export const text = z
	.string()
	.transform((value) => value.slice(0, MAX_STRING_LENGTH));

export const identityLevelSchema = z.enum(['anonymous', 'device', 'account']);

/**
 * Stamped on every event by `track()`; never supplied by a call site. The
 * names and values here are Canvasflow's own and do not depend on where an
 * event is sent: a destination that cannot take a field under its name
 * renames it in that destination's mapping, not here.
 *
 * The UI template was in the draft catalog and was removed (2026-10-05): it
 * is a property of the build, not of the app's data, and `app_id` already
 * says which app an event came from.
 */
export const envelopeSchema = z.strictObject({
	event_id: z.uuid(),
	schema_version: z.literal(SCHEMA_VERSION),
	/** When it happened on the device, in ms since epoch. */
	event_ts: z.number().int().nonnegative(),
	session_id: z.uuid(),
	/** Present at identity `device` and `account`; absent at `anonymous`. */
	reader_id: z.uuid().optional(),
	/** Identity `account` only: a hash supplied by `system`. Reserved. */
	account_id: text.optional(),
	identity_level: identityLevelSchema,
	/**
	 * The tenant app's id in `system`: which app the event came from. The
	 * same issue can appear in more than one app, so reports filter on it.
	 * Absent only on a device's first launch when the id could not be
	 * fetched (`app-id.ts`).
	 */
	app_id: text.optional(),
	build_version: text,
	display_mode: z.enum(['installed', 'browser']),
	/** The app (UI) language. */
	language: text,
	/** The device was offline when the event happened. */
	offline: z.boolean(),
});

/**
 * The content the event is **about**, which is not always the content on
 * screen: a share or a bookmark made outside the reader still names its
 * issue and article, so the call site passes these in.
 */
export const contentSchema = z.strictObject({
	issue_id: z.number().int().positive().optional(),
	issue_title: text.optional(),
	/** The language the content is read in; can differ from `language`. */
	content_language: text.optional(),
	/** The layout being read. Absent outside the reader. */
	content_type: z.enum(['replica', 'responsive']).optional(),
	article_id: z.number().int().positive().optional(),
	article_title: text.optional(),
	/** Replica: the page. Responsive: the article's position in the issue. */
	page_number: z.number().int().nonnegative().optional(),
});

export type Envelope = z.output<typeof envelopeSchema>;
export type EventContent = z.input<typeof contentSchema>;

/** The content fields an event can be required to carry. */
export const CONTENT_ID_KEYS = ['issue_id', 'article_id', 'page_number'] as const;
export type ContentIdKey = (typeof CONTENT_ID_KEYS)[number];
